
import type { ReactNode } from 'react';
import {
  ImageBackdropLayers,
  XatsPriceValue,
  cn,
} from '@/components/help-center/latest-power-shared';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import type { AceInfo, AceListingStats } from '@/lib/aces-data';
import { formatReleaseDate } from '@/lib/powers-data';
import {
  Calendar,
  CalendarClock,
  HandCoins,
  Layers,
  RefreshCw,
  Sparkles,
  Tag,
} from 'lucide-react';

const infoRowClass = cn(
  'flex w-full min-h-[42px] items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-colors duration-200 hover:border-amber-400/25 hover:from-white/[0.09]'
);

type StatRowProps = {
  icon: React.ElementType;
  label: string;
  value: ReactNode;
};

function StatRow({ icon: Icon, label, value }: StatRowProps) {
  return (
    <div className={infoRowClass}>
      <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-amber-200/75">
        <Icon className="h-3.5 w-3.5 shrink-0 text-amber-300/90" aria-hidden />
        {label}
      </span>
      <div className="min-w-0 text-right">{value}</div>
    </div>
  );
}

const statsCardShellClass = cn(
  'relative overflow-hidden rounded-2xl border border-blue-900/60 bg-blue-950/30',
  'shadow-xl shadow-black/35 ring-1 ring-amber-400/20',
  'transition-all duration-300 hover:border-amber-400/30 hover:shadow-blue-950/50'
);

type AceStatsListingProps = {
  variant: 'listing';
  stats: AceListingStats;
  className?: string;
};

type AceStatsDetailProps = {
  variant: 'detail';
  ace: AceInfo;
  className?: string;
};

export type AceStatsProps = AceStatsListingProps | AceStatsDetailProps;

export function AceStats(props: AceStatsProps) {
  const { className } = props;
  const isListing = props.variant === 'listing';

  return (
    <div className={cn('relative mx-auto w-full max-w-[420px] lg:mx-0', className)}>
      <div className={statsCardShellClass}>
        <ImageBackdropLayers className="absolute inset-0 overflow-hidden rounded-2xl" />
        <div className="pointer-events-none absolute inset-0 bg-slate-950/72" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#030023]/45 via-transparent to-[#030023]/80"
          aria-hidden
        />

        <div className="relative z-10 flex flex-col gap-4 px-5 pb-5 pt-5">
          <div className="text-center">
            <h3 className="font-poppins text-sm font-bold uppercase tracking-[0.2em] text-white md:text-base">
              {isListing ? 'Visão geral' : 'Informações'}
            </h3>
            <PortalCrystalSeparator className="mt-3.5" accentRgb="251, 191, 36" />
          </div>

          <div className="space-y-2">
            {isListing ? (
              <>
                <StatRow
                  icon={Sparkles}
                  label="Total de Aces"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.stats.totalAces}
                    </span>
                  }
                />
                <StatRow
                  icon={CalendarClock}
                  label="Último Ace lançado"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.stats.latestAceName}
                    </span>
                  }
                />
                <StatRow
                  icon={Layers}
                  label="Sistema de níveis"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.stats.levelsSystemLabel}
                    </span>
                  }
                />
              </>
            ) : (
              <>
                <StatRow
                  icon={HandCoins}
                  label="Preço na loja"
                  value={
                    <span className="inline-flex items-center justify-end gap-1 font-poppins text-xs font-semibold sm:text-sm">
                      <XatsPriceValue className="text-amber-300">
                        {props.ace.storePriceXats}
                      </XatsPriceValue>
                      <span className="text-amber-300">xats</span>
                      <span className="text-blue-100/80">/ {props.ace.subscriptionDays} dias</span>
                    </span>
                  }
                />
                <StatRow
                  icon={Tag}
                  label="Tipo"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.ace.subscriptionLabel}
                    </span>
                  }
                />
                <StatRow
                  icon={RefreshCw}
                  label="Renovação"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.ace.renewalLabel}
                    </span>
                  }
                />
                <StatRow
                  icon={Layers}
                  label="Sistema de níveis"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {props.ace.levelsLabel}
                    </span>
                  }
                />
                <StatRow
                  icon={Calendar}
                  label="Data de lançamento"
                  value={
                    <span className="font-poppins text-xs font-semibold text-white sm:text-sm">
                      {formatReleaseDate(props.ace.releasedAt)}
                    </span>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
